/**
 * The rooms that can boot and the beats that are staged in them.
 *
 * **This moved out of `main.ts` in the shell session (D-102), and the reason is
 * the save.** A checkpoint names a beat, the title screen's Continue resumes
 * one, and the hidden jump lists them by title — so three new callers needed the
 * table, and every one of them runs *before* the world is built. Importing it
 * from `main.ts` would have meant importing the boot.
 *
 * The alternative was for the save to carry its own list of beats, and that is
 * the failure this project keeps having: two derivations of one fact that agree
 * until somebody edits one of them (see `wearForBeat`, which exists for exactly
 * this reason). **There is one table. `main.ts` selects from it, the save names
 * into it, and the jump menu renders it.**
 *
 * The comments below came with the move rather than being rewritten; they were
 * paid for by bugs and they are still the reason each entry is shaped as it is.
 */

/**
 * The rooms that can boot. Two are content; two are measuring instruments.
 *
 * D-004 ended the placeholder exemption for *shipped scenes*, and a fixture
 * reachable only by a query parameter the game never sets is not one. The
 * grey-box room is what `movement.spec.ts` asserts against and is the reason
 * anyone can trust a change to the controller; the softlock fixture's sealed trap
 * and tight escape lane are the only way that regression gate can prove both
 * verdicts. Deleting either would not simplify anything, it would delete the
 * evidence.
 */
export type RoomId = 'farrowing' | 'fattening' | 'service' | 'cull' | 'yard' | 'hedgerow' | 'hedgerow-night' | 'wool-shed' | 'office' | 'yard-dawn' | 'yard-fails' | 'hill' | 'testroom' | 'softlock-fixture';

export const ROOM_IDS: readonly RoomId[] = ['farrowing', 'fattening', 'service', 'cull', 'yard', 'hedgerow', 'hedgerow-night', 'wool-shed', 'office', 'yard-dawn', 'yard-fails', 'hill', 'testroom', 'softlock-fixture'];

export function isRoomId(value: string | null): value is RoomId {
  return value !== null && (ROOM_IDS as readonly string[]).includes(value);
}

/** One authored room section within a beat. Parts are numbered from one at the URL boundary. */
export interface BeatPart {
  readonly room: RoomId;
}

/** One beat: what it is called, its room parts, and what follows the final part. */
export interface BeatEntry {
  readonly title: string;
  /**
   * The rooms in this beat, in play order.
   *
   * A part change is a navigation, just as a beat change is: rooms do not share
   * a world, so this table is the one place that can say both which room boots
   * and whether completing it stays in the same beat. Keeping a separate room
   * list in routing would recreate the two-derivations failure this module was
   * moved here to prevent.
   */
  readonly parts: readonly BeatPart[];
  /**
   * The beat this one hands over to when it ends, or absent at the end of what
   * is built. See the handover in the render step.
   *
   * This table is the ONLY place the order of Act 1 is written down. A beat
   * itself does not know it has a successor — `src/story/` may not know what a
   * room is, let alone which one comes next — so the running order lives with
   * the room assignment, which is the other fact of the same kind.
   */
  readonly next?: number;
}

/** Return a one-based part only when the table has actually authored it. */
export function partForBeat(entry: BeatEntry, part: number): BeatPart | null {
  if (!Number.isInteger(part) || part < 1) return null;
  return entry.parts[part - 1] ?? null;
}

/**
 * `?beat=N`, and it is the whole selector.
 *
 * **Beat 0 is not a debug convenience, it is a correctness requirement.** It is
 * what every spec that is about the CONTROLLER rather than about the story boots
 * with. A beat spawns her inside a pen, may hold the camera, and moves scripted
 * bodies about on timers; a movement test measuring her stopping distance under
 * all that is measuring the beat, and a perf test measuring the frame budget with
 * a scripted mover in it is measuring the wrong scene.
 *
 * It stays in the farrowing hall because that is what every existing spec that
 * uses it expects. A `?beat=0` for the fattening hall would be a fifth entry and
 * a fifth thing to keep true; `?room=` already exists for anybody who wants a
 * room without a story, and beat 3 without its director is exactly that.
 */
export const BEATS: Readonly<Record<number, BeatEntry>> = {
  0: { title: 'the hall, no beat', parts: [{ room: 'farrowing' }] },
  1: { title: 'Born', parts: [{ room: 'farrowing' }], next: 2 },
  2: { title: 'The Weakest Latch', parts: [{ room: 'farrowing' }], next: 3 },
  // Beat 3 had no `next` until beat 4 shipped and replaced that ending: it used
  // to be the honest end of what was built rather than an omission — it ended on
  // black and stayed there. The card that arrives on the way IN to a beat is a
  // different thing and is now built — see `chapterCard` below and D-085; what
  // this paragraph was refusing was a card used to hide a missing successor,
  // which is no longer the situation.
  3: { title: 'Otto', parts: [{ room: 'fattening' }], next: 4 },
  4: { title: 'The Service Corridor', parts: [{ room: 'service' }], next: 5 },
  5: { title: 'The Cull Corridor', parts: [{ room: 'cull' }], next: 6 },
  6: { title: 'The Loading Yard', parts: [{ room: 'yard' }], next: 7 },
  // Beat 7 is the end of ACT 1, and `next: 8` is what carries the save era past
  // it rather than making it a dead end: D-102 puts the shell (splash, title,
  // content notice, Gentle Mode, save and pause-menu restart) immediately after
  // this beat, because this is the first moment Act 1 is a complete thing to
  // save, and the last moment the save format can be designed rather than
  // retrofitted. It stopped being the honest end of what is built the session
  // beat 8 shipped; the comment saying otherwise outlived that by several more.
  7: { title: 'Out', parts: [{ room: 'hedgerow' }], next: 8 },
  // **Beat 8 is where Act 1 hands over to Act 2, and it is the first beat with
  // two rooms.** `?beat=8` is the relit cull corridor she walks back in through;
  // `?beat=8&part=2` is the fattening hall with Otto's pen empty. See
  // `partForBeat` above for why both live in this table rather than in routing.
  //
  // Beat 9 now exists as its first-cut route, so this hand-over continues into
  // Act 2 rather than ending Blackout's fade with a confidently wrong comment.
  8: { title: 'Blackout', parts: [{ room: 'cull' }, { room: 'fattening' }], next: 9 },
  // The exterior bell/worker round is the documented first cut: this route is
  // puzzle shed, then rest shed. Beat 10 now owns the exterior again, at night.
  9: { title: 'Wool and Wire', parts: [{ room: 'wool-shed' }, { room: 'wool-shed' }], next: 10 },
  // Beat 10 is beat 7's field at night, and a room id of its own for the reason
  // `yard-dawn` is one (D-202): the same geometry at another hour, which a room
  // module cannot tell from the day when it is only told the part. It shares
  // beat 7's origin and metres, so her notes stay on the `hedgerow` sheet.
  10: { title: 'Twelve Out', parts: [{ room: 'hedgerow-night' }], next: 11 },
  11: { title: 'The Tally', parts: [{ room: 'office' }], next: 12 },
  // Beat 12 is beat 6's yard at dawn, and a room id of its own: `yard` is the
  // storm, wired through beat 6's director, and `yard-dawn` is the same
  // geometry as a module (`src/game/rooms/yardDawn.ts` says why).
  12: { title: 'Dawn Transport', parts: [{ room: 'yard-dawn' }], next: 13 },
  13: { title: 'It Fails', parts: [{ room: 'yard-fails' }], next: 14 },
  // Beat 14 is the last beat of the game, and its missing `next` is the end of
  // the game rather than of what is built: the shell plays the credits after it
  // (D-102), on the bell's last stroke. `hill` is the hedgerow's far field as a
  // module (`src/game/rooms/hill.ts` says why it is its own id).
  14: { title: 'Ending', parts: [{ room: 'hill' }] },
};

/**
 * The part a boot URL selects, or null when it names one nobody authored.
 *
 * **A storyless room boot may name a room's part.** `?beat=0&room=wool-shed&part=2`
 * is how `shots:room`, the census and containment see beat 9's rest state without
 * arming beat 9's black fade overlay. Beat 0 has one part, so checking the part
 * against beat 0 made that boot throw, and `PART=2 shots:room` photographed
 * nothing. The part must still be one that some beat authors in the named room,
 * so a typo fails at boot instead of silently booting part 1
 * (`story.spec.ts`, both directions).
 */
export function partForBoot(beatNumber: number, partNumber: number, requestedRoom: string | null): BeatPart | null {
  const beat = BEATS[beatNumber];
  if (beat === undefined) return null;
  if (beatNumber !== 0 || partNumber === 1 || !isRoomId(requestedRoom)) return partForBeat(beat, partNumber);
  const authored = Object.values(BEATS).some((entry) => partForBeat(entry, partNumber)?.room === requestedRoom);
  return authored ? { room: requestedRoom } : null;
}
